"use client"

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    query,
    where,
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { useInvoice } from './InvoiceContext';

export type Customer = {
    id?: string;
    name: string;
    email: string;
    phone: string;
    address?: string;
    notes?: string;
    category?: string;
    createdAt: Date;
    updatedAt: Date;
    createdBy?: string;
    budgetId: string;
    totalInvoices?: number;
    totalSpent?: number;
    lastInvoiceDate?: Date;
};

type CustomerState = {
    customers: Customer[];
    loading: boolean;
    error: string | null;
};

type CustomerAction =
    | { type: 'LOAD_CUSTOMERS'; payload: Customer[] }
    | { type: 'ADD_CUSTOMER'; payload: Customer }
    | { type: 'UPDATE_CUSTOMER'; payload: Customer }
    | { type: 'DELETE_CUSTOMER'; payload: string }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null };

type CustomerContextType = {
    state: CustomerState;
    loadCustomers: () => Promise<void>;
    addCustomer: (customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Customer>;
    updateCustomer: (customer: Customer) => Promise<void>;
    deleteCustomer: (customerId: string) => Promise<void>;
    clearError: () => void;
};

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

const initialState: CustomerState = {
    customers: [],
    loading: false,
    error: null,
};

function customerReducer(state: CustomerState, action: CustomerAction): CustomerState {
    switch (action.type) {
        case 'LOAD_CUSTOMERS':
            return { ...state, customers: action.payload, loading: false };
        case 'ADD_CUSTOMER':
            return { ...state, customers: [...state.customers, action.payload], loading: false };
        case 'UPDATE_CUSTOMER':
            return {
                ...state,
                customers: state.customers.map((customer) =>
                    customer.id === action.payload.id ? action.payload : customer
                ),
                loading: false,
            };
        case 'DELETE_CUSTOMER':
            return {
                ...state,
                customers: state.customers.filter((customer) => customer.id !== action.payload),
                loading: false,
            };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload, loading: false };
        default:
            return state;
    }
}

export function CustomerProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(customerReducer, initialState);
    const { state: budgetState } = useBudgetManager();
    const { state: invoiceState } = useInvoice();

    const updateCustomerInvoiceTotals = useCallback(async () => {
        if (!state.customers.length || !invoiceState.invoices.length) return;

        const customerUpdates = state.customers.map(customer => {
            const customerInvoices = invoiceState.invoices.filter(
                invoice => invoice.customerId === customer.id
            );

            const totalInvoices = customerInvoices.length;
            const totalSpent = customerInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
            const lastInvoiceDate = customerInvoices.length > 0
                ? new Date(Math.max(...customerInvoices.map(inv => inv.createdAt.getTime())))
                : undefined;

            return {
                ...customer,
                totalInvoices,
                totalSpent,
                lastInvoiceDate,
                updatedAt: new Date()
            };
        });

        try {
            const batch = writeBatch(db);

            customerUpdates.forEach(customer => {
                if (customer.id) {
                    const customerRef = doc(db, 'customers', customer.id);
                    batch.update(customerRef, {
                        totalInvoices: customer.totalInvoices,
                        totalSpent: customer.totalSpent,
                        lastInvoiceDate: customer.lastInvoiceDate ? Timestamp.fromDate(customer.lastInvoiceDate) : null,
                        updatedAt: Timestamp.now()
                    });
                }
            });

            await batch.commit();
            dispatch({ type: 'LOAD_CUSTOMERS', payload: customerUpdates });

        } catch (error) {
            console.error('Error updating customer totals:', error);
            dispatch({ type: 'SET_ERROR', payload: 'Failed to update customer totals' });
        }
    }, [state.customers, invoiceState.invoices]);

    // Modify loadCustomers to include invoice data
    const loadCustomers = useCallback(async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const user = auth.currentUser;
            if (!user) throw new Error('User not authenticated');

            const currentBudget = budgetState.currentBudget;
            if (!currentBudget || !currentBudget.id) {
                console.warn('No budget selected or budget has no ID');
                dispatch({ type: 'LOAD_CUSTOMERS', payload: [] });
                return;
            }

            const customersQuery = query(
                collection(db, 'customers'),
                where('createdBy', '==', user.uid),
                where('budgetId', '==', currentBudget.id)
            );

            const querySnapshot = await getDocs(customersQuery);
            const customers: Customer[] = querySnapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    ...data,
                    id: doc.id,
                    createdAt: data.createdAt.toDate(),
                    updatedAt: data.updatedAt.toDate(),
                    lastInvoiceDate: data.lastInvoiceDate?.toDate(),
                    totalInvoices: data.totalInvoices || 0,
                    totalSpent: data.totalSpent || 0
                } as Customer;
            });

            dispatch({ type: 'LOAD_CUSTOMERS', payload: customers });

            // Update totals after loading customers
            await updateCustomerInvoiceTotals();
        } catch (error) {
            console.error('Error loading customers:', error);
            dispatch({ type: 'SET_ERROR', payload: 'Failed to load customers' });
        }
    }, [budgetState.currentBudget?.id]);

    const addCustomer = useCallback(async (customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const user = auth.currentUser;
            if (!user) throw new Error('User not authenticated');

            const currentBudget = budgetState.currentBudget;
            if (!currentBudget) throw new Error('No budget selected');

            const newCustomer = {
                ...customer,
                createdBy: user.uid,
                budgetId: currentBudget.id,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                totalInvoices: 0,
                totalSpent: 0,
            };

            const docRef = await addDoc(collection(db, 'customers'), newCustomer);
            const addedCustomer: Customer = {
                ...newCustomer,
                id: docRef.id,
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            dispatch({ type: 'ADD_CUSTOMER', payload: addedCustomer });

            return addedCustomer;
        } catch (error) {
            console.error('Error adding customer:', error);
            dispatch({ type: 'SET_ERROR', payload: 'Failed to add customer' });
            throw error;
        }
    }, [budgetState.currentBudget]);

    const updateCustomer = useCallback(async (customer: Customer) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            if (!customer.id) throw new Error('Customer ID is required for update');

            const { id, ...updateData } = customer;
            await updateDoc(doc(db, 'customers', id), {
                ...updateData,
                updatedAt: Timestamp.now(),
            });

            dispatch({ type: 'UPDATE_CUSTOMER', payload: customer });
        } catch (error) {
            console.error('Error updating customer:', error);
            dispatch({ type: 'SET_ERROR', payload: 'Failed to update customer' });
            throw error;
        }
    }, []);

    const deleteCustomer = useCallback(async (customerId: string) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            await deleteDoc(doc(db, 'customers', customerId));
            dispatch({ type: 'DELETE_CUSTOMER', payload: customerId });
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Failed to delete customer' });
        }
    }, []);

    const clearError = useCallback(() => {
        dispatch({ type: 'SET_ERROR', payload: null });
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                loadCustomers();
            } else {
                dispatch({ type: 'LOAD_CUSTOMERS', payload: [] });
            }
        });

        return () => unsubscribe();
    }, [loadCustomers]);


    return (
        <CustomerContext.Provider
            value={{
                state,
                loadCustomers,
                addCustomer,
                updateCustomer,
                deleteCustomer,
                clearError,
            }}
        >
            {children}
        </CustomerContext.Provider>
    );
}

export function useCustomer(): CustomerContextType {
    const context = useContext(CustomerContext);
    if (!context) {
        throw new Error('useCustomer must be used within a CustomerProvider');
    }
    return context;
}
